/**
 * Client-side legality hint for dimming unplayable cards in the hand.
 *
 * This is only a hint — the server is authoritative.  We compute a
 * conservative "selectable" set: which individual cards the player is
 * allowed to pick. Combo legality (correct count / structure) is
 * validated on submit.
 *
 * Rules (when it's your turn to follow):
 *   - If leading, all cards selectable.
 *   - If you have cards in the led effective suit, only those are
 *     selectable.
 *   - If the lead is a pair AND you have any pair in the led suit,
 *     only cards that are part of a pair in the led suit are selectable.
 *   - If the lead is a tractor AND you have a matching tractor in the
 *     led suit, only that tractor's cards are selectable. Otherwise the
 *     pair-rule above applies.
 *   - If you are void in the led suit, anything goes.
 */

import type { CardId, ShengJiPublicState } from "../../../../shared/games/shengji.js";

type SuitLetter = "C" | "D" | "H" | "S" | "J";

function isJoker(id: CardId): boolean {
  return id === "SJ" || id === "BJ";
}

function rankOf(id: CardId): string {
  if (id === "SJ") return "sj";
  if (id === "BJ") return "bj";
  return id[0]!;
}

function suitOf(id: CardId): SuitLetter {
  if (id === "SJ" || id === "BJ") return "J";
  return id[1]! as SuitLetter;
}

function isTrump(id: CardId, trumpRank: string, trumpSuit: SuitLetter | null): boolean {
  if (isJoker(id)) return true;
  if (rankOf(id) === trumpRank) return true;
  return trumpSuit !== null && suitOf(id) === trumpSuit;
}

/** "TRUMP" or the suit letter for the card's effective suit. */
function effectiveSuit(id: CardId, trumpRank: string, trumpSuit: SuitLetter | null): string {
  if (isTrump(id, trumpRank, trumpSuit)) return "TRUMP";
  return suitOf(id);
}

// Rank ordering for non-trump suits (the suit ladder). Trump suit cards have a
// more elaborate ordering with jokers + trump-rank cards on top — we only
// implement the structural-match hint for non-trump-suit leads (the common
// case). For trump-suit leads we fall back to "any in-suit card is selectable",
// which the server still validates on submit.
const RANK_INDEX: Record<string, number> = {
  "2": 0, "3": 1, "4": 2, "5": 3, "6": 4, "7": 5, "8": 6, "9": 7,
  "T": 8, "J": 9, "Q": 10, "K": 11, "A": 12,
};

/**
 * True if two non-trump ranks are consecutive in the trump-skipped order.
 * Example: with trumpRank=5, the ranks 4 and 6 are consecutive in clubs.
 */
function ranksAdjacent(r1: string, r2: string, trumpRank: string): boolean {
  const i1 = RANK_INDEX[r1];
  const i2 = RANK_INDEX[r2];
  const t = RANK_INDEX[trumpRank];
  if (i1 === undefined || i2 === undefined) return false;
  const diff = Math.abs(i1 - i2);
  if (diff === 1) return true;
  // Skipping the trump-rank: 4 and 6 are adjacent if trump is 5.
  if (diff === 2 && t !== undefined && Math.min(i1, i2) < t && t < Math.max(i1, i2)) return true;
  return false;
}

type LeadKind = "single" | "pair" | "tractor" | "other";

/**
 * Classify the lead's structure. We only recognise pure pair and pure
 * tractor leads here — other shapes (throws, mixed) defer to the looser
 * "any in-suit card" hint, which the server then validates strictly.
 */
function describeLead(cards: CardId[], trumpRank: string): LeadKind {
  if (cards.length === 1) return "single";
  if (cards.length === 2 && cards[0] === cards[1]) return "pair";
  if (cards.length >= 4 && cards.length % 2 === 0) {
    const counts = new Map<CardId, number>();
    for (const c of cards) counts.set(c, (counts.get(c) ?? 0) + 1);
    if ([...counts.values()].every((n) => n === 2)) {
      const pairCards = [...counts.keys()];
      // Trump-suit / joker leads aren't handled (cards span trump ladder).
      if (pairCards.some(isJoker)) return "other";
      const ranks = pairCards.map(rankOf).sort((a, b) => (RANK_INDEX[a] ?? 0) - (RANK_INDEX[b] ?? 0));
      for (let i = 1; i < ranks.length; i++) {
        if (!ranksAdjacent(ranks[i - 1]!, ranks[i]!, trumpRank)) return "other";
      }
      return "tractor";
    }
  }
  return "other";
}

/** Cards in `inSuit` that are at least one half of a pair (count >= 2). */
function pairMaterialIds(inSuit: CardId[]): Set<CardId> {
  const counts = new Map<CardId, number>();
  for (const c of inSuit) counts.set(c, (counts.get(c) ?? 0) + 1);
  const out = new Set<CardId>();
  for (const [c, n] of counts) {
    if (n >= 2) out.add(c);
  }
  return out;
}

/**
 * If `inSuit` contains a tractor of length `tractorLen` (number of pairs),
 * return the set of card ids that make it up. We greedily pick the lowest
 * matching run — the server doesn't care which tractor we use; it just
 * wants a tractor of sufficient length.
 */
function findMatchingTractorIds(
  inSuit: CardId[],
  tractorLen: number,
  trumpRank: string
): Set<CardId> | null {
  const counts = new Map<CardId, number>();
  for (const c of inSuit) counts.set(c, (counts.get(c) ?? 0) + 1);
  const pairCards = [...counts.entries()]
    .filter(([_, n]) => n >= 2)
    .map(([c]) => c)
    .filter((c) => !isJoker(c));
  if (pairCards.length < tractorLen) return null;
  pairCards.sort((a, b) => (RANK_INDEX[rankOf(a)] ?? 0) - (RANK_INDEX[rankOf(b)] ?? 0));
  for (let i = 0; i + tractorLen <= pairCards.length; i++) {
    let run = true;
    for (let j = 1; j < tractorLen; j++) {
      if (!ranksAdjacent(rankOf(pairCards[i + j - 1]!), rankOf(pairCards[i + j]!), trumpRank)) {
        run = false;
        break;
      }
    }
    if (run) {
      const result = new Set<CardId>();
      for (let j = 0; j < tractorLen; j++) result.add(pairCards[i + j]!);
      return result;
    }
  }
  return null;
}

export function computeLegalCardSet(
  hand: CardId[],
  state: ShengJiPublicState,
  yourSeat: number
): Set<CardId> {
  const out = new Set<CardId>();
  if (state.phase !== "play") {
    for (const c of hand) out.add(c);
    return out;
  }
  if (state.currentSeat !== yourSeat) {
    return out;
  }
  const trumpRank = state.trumpRank;
  const trumpSuit = state.trumpSuit;
  if (state.currentTrickPlays.length === 0) {
    for (const c of hand) out.add(c);
    return out;
  }
  const lead = state.currentTrickPlays[0]!;
  if (lead.cards.length === 0) {
    for (const c of hand) out.add(c);
    return out;
  }

  const ledSuit = effectiveSuit(lead.cards[0]!, trumpRank, trumpSuit);
  const handInSuit = hand.filter((c) => effectiveSuit(c, trumpRank, trumpSuit) === ledSuit);

  // Void in the led suit: any card works.
  if (handInSuit.length === 0) {
    for (const c of hand) out.add(c);
    return out;
  }

  // Short of the lead's count in the led suit: you MUST include all
  // your in-suit cards AND fill the remaining slots with cards from
  // other suits (you can't form the required structure anyway, so
  // structure rules don't narrow the choice). Make the whole hand
  // selectable — the in-suit cards are forced-in by the count
  // requirement and the user picks fillers to round out the play.
  if (handInSuit.length < lead.cards.length) {
    for (const c of hand) out.add(c);
    return out;
  }

  const leadKind = describeLead(lead.cards, trumpRank);

  // For single leads (or unrecognised lead shapes), only the suit rule applies.
  if (leadKind === "single" || leadKind === "other") {
    for (const c of handInSuit) out.add(c);
    return out;
  }

  // Pair / tractor lead — narrow further if we hold matching material.
  if (leadKind === "tractor") {
    const tractorCards = findMatchingTractorIds(handInSuit, lead.cards.length / 2, trumpRank);
    if (tractorCards) return tractorCards;
    // No matching tractor — fall through to pair handling.
  }

  // Pair lead (or tractor without matching tractor): if we have any pair
  // in the led suit, must use a pair. Only paired cards are selectable.
  const pairs = pairMaterialIds(handInSuit);
  if (pairs.size > 0) return pairs;

  // No pairs in suit either — any in-suit card.
  for (const c of handInSuit) out.add(c);
  return out;
}

/** Validate a single-card declaration that *could* be made now. */
export function isDeclarableSingle(card: CardId, trumpRank: string): boolean {
  if (isJoker(card)) return false;
  return rankOf(card) === trumpRank;
}

/** Validate a pair of cards forms a (possibly stronger) declaration. */
export function isDeclarablePair(
  cards: CardId[],
  trumpRank: string
): { ok: true; strength: number; trumpSuit: SuitLetter | null } | { ok: false } {
  if (cards.length !== 2) return { ok: false };
  const [a, b] = cards;
  if (a !== b) return { ok: false };
  if (a === "SJ") return { ok: true, strength: 3, trumpSuit: null };
  if (a === "BJ") return { ok: true, strength: 4, trumpSuit: null };
  if (!isJoker(a!) && rankOf(a!) === trumpRank) {
    return { ok: true, strength: 2, trumpSuit: suitOf(a!) };
  }
  return { ok: false };
}
